import { CertificationRequirementToggle } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/components/CertificationRequirementToggle.tsx';
import { ToggleRequirementItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/components/ToggleRequirementItem.tsx';
import { KycSection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/styles.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	CERTIFICATION_FLAG_BY_PROOF,
	type KycProofFlag,
} from '@dealstack/shared/lib/common/business/kycCertification';
import type { RequirementFlag } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/useKYCRequirementsController.ts';
import { useKycCertificationEnabled } from '@dealstack/frontend/shared/hooks/useKycCertificationEnabled/useKycCertificationEnabled.hook.ts';

/** Shares Programme settings → KYC & AML's wording, so the two screens cannot drift apart. */
const PROOF_LABELS = {
	isProofOfIdentityRequired: {
		label: 'kyc_settings.proofs.identity.label',
		description: 'kyc_settings.proofs.identity.description',
	},
	isProofOfAddressRequired: {
		label: 'kyc_settings.proofs.address.label',
		description: 'kyc_settings.proofs.address.description',
	},
	isProofOfInvestmentVehicleRequired: {
		label: 'kyc_settings.proofs.vehicle.label',
		description: 'kyc_settings.proofs.vehicle.description',
	},
} as const;

interface Props {
	flag: KycProofFlag;
	checked: boolean;
	certificationChecked: boolean;
	isSaving: (flag: RequirementFlag) => boolean;
	onProofChange: (flag: KycProofFlag, value: boolean) => void;
	onCertificationChange: (value: boolean) => void;
	certificationDataTestId: string;
}

/** One proof requirement with the certification it owns nested beneath it. */
export const ProofRequirementToggle = ({
	flag,
	checked,
	certificationChecked,
	isSaving,
	onProofChange,
	onCertificationChange,
	certificationDataTestId,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	// Programme creation has no programme yet, so the flag resolves on the environment's own
	// value here rather than on a mipId rule.
	const { certificationEnabled } = useKycCertificationEnabled();
	const certificationFlag = CERTIFICATION_FLAG_BY_PROOF[flag];

	return (
		<KycSection $noMaxWidth $noPadding>
			<ToggleRequirementItem
				title={tMipManage(PROOF_LABELS[flag].label)}
				description={tMipManage(PROOF_LABELS[flag].description)}
				checked={checked}
				disabled={isSaving(flag)}
				onToggle={() => onProofChange(flag, !checked)}
				showBorder={false}
			/>
			{certificationEnabled && checked && (
				<CertificationRequirementToggle
					checked={certificationChecked}
					disabled={isSaving(certificationFlag)}
					onToggle={() => onCertificationChange(!certificationChecked)}
					dataTestId={certificationDataTestId}
				/>
			)}
		</KycSection>
	);
};
